import {
  Ref,
  useState,
  useMemo,
  useCallback,
  useImperativeHandle,
  ReactNode,
} from "react";
import { useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import styles from "./TestbuyViewerFragment.module.css";
import { ViewerFragmentCommonProps } from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragment";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import {
  isLoading,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import TaskState from "@coworker/apprestructured/src/task-management/types/TaskState";
import { useTranslation } from "@coworker/locales";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { AlertModal } from "@coworker/apprestructured/src/shared/components/AlertModal/AlertModal";
import { TestbuySummaryInfo } from "@coworker/apprestructured/src/testbuy/components/TestbuySummaryInfo/TestbuySummaryInfo";
import { useCreateTestbuy } from "@coworker/apprestructured/src/testbuy/hooks/useTestbuyV2";
import { useGetTestbuy } from "@coworker/apprestructured/src/testbuy/hooks/useTestbuyV2";
import {
  useStoreId,
  useCountryId,
  useMyStore,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { invalidateTaskQueries } from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";
import LoadingViewerFragment from "../LoadingViewerFragment/LoadingViewerFragment";
import { useUserPreference } from "@coworker/app/src/hooks/useProfilePreferencesQuery";
import profilePreferences from "@coworker/enums/profilePreferences";
import { mapCreateTestbuyErrorToCopy } from "@coworker/apprestructured/src/testbuy/services/createTestbuyErrorMapper";
import {
  allCriteriaAnswered,
  allCriteriaOk,
} from "@coworker/app/src/components/TestBuy/CommonComponents/article.helpers";
import { Criterion } from "@coworker/types/lib/testbuy";
import { ViewerFragmentHandle } from "../ViewerFragment";

export type TestbuyViewerFragmentConfigProps = Record<string, never>;

function formatAreasList(
  items: string[],
  t: (key: string, params?: Record<string, string>) => string
): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} ${t("andString")} ${items.slice(-1)}`;
}

function buildInsufficientHfbsMessage(
  hfbList: string[],
  t: (key: string, params?: Record<string, string>) => string
): ReactNode {
  const hfbLabels = formatAreasList(hfbList, t);
  return (
    <>
      <div style={{ marginTop: "8px" }}>
        <FixaText>{t("insufficientHfbsDescriptionPart1String")}</FixaText>
      </div>
      <div style={{ marginTop: "8px" }}>
        <FixaText>
          {t("insufficientHfbsDescriptionPart2String", { hfbLabels })}
        </FixaText>
      </div>
      <div style={{ marginTop: "8px" }}>
        <FixaText>{t("insufficientHfbsDescriptionPart3String")}</FixaText>
      </div>
    </>
  );
}

type TestbuyViewerFragmentProps = {
  configProps?: TestbuyViewerFragmentConfigProps;
  commonProps: ViewerFragmentCommonProps;
  ref?: Ref<ViewerFragmentHandle>;
};

type TestbuyActionButtonProps = {
  task: Record<string, unknown> | Loading;
};

type ErrorModalState = {
  visible: boolean;
  title: string;
  message: string | ReactNode;
};

function TestbuyActionButton({ task }: TestbuyActionButtonProps) {
  // hooks
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  const [errorModal, setErrorModal] = useState<ErrorModalState>({
    visible: false,
    title: "",
    message: "",
  });
  const [isOpeningTestbuy, setIsOpeningTestbuy] = useState(false);
  const storeId = useStoreId();
  const countryId = useCountryId();
  const { mutateAsync: createTestbuy, isPending: isCreatingTestbuy } =
    useCreateTestbuy();

  const store = useMyStore();
  const storeLocale = store?.primaryLocale || "";
  const [userLanguage] = useUserPreference(
    profilePreferences.LANGUAGE,
    storeLocale
  );

  // data
  const taskState = getTypedValue<TaskState>(getByPath(task, "state"));
  const taskId = getTypedValue<string>(getByPath(task, "id"));
  const testbuyId = getTypedValue<string>(
    getByPath(task, "details.testbuy_id")
  );
  const areas = getTypedValue<string[]>(getByPath(task, "details.areas"));
  const isStarted = !isLoading(testbuyId) && !!testbuyId;

  const userOrStoreLanguage = userLanguage || storeLocale;
  const locale = userOrStoreLanguage ?? "en_GB";

  const buttonText = useMemo(() => {
    if (taskState === "completed" || taskState === "closed") {
      return t("seeTestbuyString");
    }
    if (isStarted) {
      return t("continueTestbuyString");
    }
    return t("startTestbuyString");
  }, [taskState, isStarted, t]);

  // event handlers
  const openTestbuySheet = useCallback(
    (id: string) => {
      setSearchParams((params) => {
        // Replace this task viewer with the testbuy sheet instead of stacking them:
        // testbuyId and taskViewer are never both set at the same time.
        params.delete("taskViewer");
        params.set("testbuyId", id);
        return params;
      });
    },
    [setSearchParams]
  );

  const showAlertModal = useCallback(
    (title: string, message: string | ReactNode) => {
      setErrorModal({ visible: true, title, message });
    },
    []
  );

  const handleCreateTestbuy = useCallback(async () => {
    if (isLoading(taskId) || !taskId) return;
    setIsOpeningTestbuy(true);
    try {
      const response = await createTestbuy({
        taskId,
        storeId,
        countryId,
        locale,
      });

      if (response?.testBuyId) {
        await invalidateTaskQueries(queryClient);
        openTestbuySheet(response.testBuyId);
        return;
      } else if (response?.insufficientHfbs?.length) {
        const hfbList = response.insufficientHfbs.map((h) =>
          h.hfb ? `HFB ${h.hfb}` : t("wholeStoreString")
        );
        const message = buildInsufficientHfbsMessage(hfbList, t);
        showAlertModal(t("noArticlesAvailableString"), message);
      }
    } catch (error) {
      const { titleKey } = mapCreateTestbuyErrorToCopy(error);
      // Extract HFB numbers from area values like "depts=20" -> "HFB 20"; "whole=whole" -> whole store
      const hfbList =
        !isLoading(areas) && areas
          ? areas.map((a) => {
              if (a === "whole=whole") return t("wholeStoreString");
              const match = a.match(/depts=(\d+)/);
              return match ? `HFB ${match[1]}` : a;
            })
          : [];
      const message = buildInsufficientHfbsMessage(hfbList, t);
      showAlertModal(t(titleKey), message);
    }
    setIsOpeningTestbuy(false);
  }, [
    taskId,
    storeId,
    countryId,
    locale,
    areas,
    createTestbuy,
    openTestbuySheet,
    showAlertModal,
    t,
    queryClient,
  ]);

  const handleClick = useCallback(() => {
    if (isStarted && !isLoading(testbuyId)) {
      openTestbuySheet(testbuyId);
    } else {
      handleCreateTestbuy();
    }
  }, [isStarted, testbuyId, openTestbuySheet, handleCreateTestbuy]);

  const handleCloseErrorModal = useCallback(() => {
    setErrorModal((prev) => ({ ...prev, visible: false }));
  }, []);

  return (
    <>
      <FixaButton
        className={styles["btn"]}
        type="emphasised"
        text={buttonText}
        onClick={handleClick}
        loading={isCreatingTestbuy || isOpeningTestbuy}
      />
      <AlertModal
        visible={errorModal.visible}
        onClose={handleCloseErrorModal}
        title={errorModal.title}
        message={errorModal.message}
      />
    </>
  );
}

function TestbuyViewerFragment({
  commonProps,
  ref,
}: TestbuyViewerFragmentProps) {
  const { task, showValidation } = commonProps;
  const { t } = useTranslation();
  const testbuyId = getTypedValue<string>(
    getByPath(task, "details.testbuy_id")
  );
  const isStarted = !isLoading(testbuyId) && !!testbuyId;

  const strategy = getTypedValue<string>(getByPath(task, "details.strategy"));

  const articlesCountFromTask = getTypedValue<number>(
    getByPath(task, "details.articles_count")
  );
  const customArticles = getTypedValue<string[]>(
    getByPath(task, "details.custom_articles")
  );

  const areas = getTypedValue<string[]>(getByPath(task, "details.areas"));

  const includeEds = getTypedValue<boolean>(
    getByPath(task, "details.include_eds")
  );
  const includeOutOfStock = getTypedValue<boolean>(
    getByPath(task, "details.include_oos")
  );

  // If testbuy exists, fetch it to get the real article count
  const { data: testbuy } = useGetTestbuy(
    !isLoading(testbuyId) && testbuyId ? testbuyId : null
  );

  const articles = testbuy?.articles ?? [];
  const hasUntestedArticles = articles.some(
    (article) => !allCriteriaAnswered((article.criteria ?? []) as Criterion[])
  );
  const hasMissingFollowUpTasks = articles.some((article) => {
    const criteria = (article.criteria ?? []) as Criterion[];
    return (
      !article.followUpTaskId &&
      allCriteriaAnswered(criteria) &&
      !allCriteriaOk(criteria)
    );
  });
  const canBeCompleted =
    isStarted && !hasUntestedArticles && !hasMissingFollowUpTasks;

  useImperativeHandle(
    ref,
    () => ({
      valid: canBeCompleted,
      canEdit: !isStarted,
    }),
    [canBeCompleted, isStarted]
  );

  if (isLoading(task)) {
    return null;
  }

  if (
    isLoading(strategy) ||
    isLoading(areas) ||
    isLoading(articlesCountFromTask) ||
    isLoading(customArticles) ||
    isLoading(includeEds) ||
    isLoading(includeOutOfStock)
  ) {
    return <LoadingViewerFragment />;
  }

  // Use the actual article count from the testbuy if available,
  // otherwise fall back to the task's articles_count
  const articlesCount =
    strategy === "CUSTOM"
      ? customArticles.length
      : (testbuy?.articles?.length ?? articlesCountFromTask);

  return (
    <div className={`${styles["card"]} testbuy-viewer-fragment-card`}>
      <TestbuySummaryInfo
        articlesCount={articlesCount}
        areas={areas}
        strategy={strategy}
        includeEds={!!includeEds}
        includeOutOfStock={!!includeOutOfStock}
      />
      <TestbuyActionButton task={task} />
      {showValidation && !canBeCompleted && (
        <FixaText textStyle="Body.M" className={styles["validation-error"]}>
          {t("testbuyNotAllArticlesTestedString")}
        </FixaText>
      )}
    </div>
  );
}

export default TestbuyViewerFragment;
